export const isHtmlElement = (node: Node): node is HTMLElement =>
  node.nodeType === Node.ELEMENT_NODE;
export const isTextNode = (node: Node): node is Text => node.nodeType === Node.TEXT_NODE;
export const isCdataSection = (node: Node): node is CDATASection =>
  node.nodeType === Node.CDATA_SECTION_NODE;
export const isTableElement = (node: Node): node is HTMLTableElement => node.nodeName === 'table';
export const isHeadingElement = (node: Node): node is HTMLHeadingElement =>
  ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(node.nodeName);
export const isListElement = (node: Node): node is HTMLUListElement | HTMLDListElement =>
  ['list', 'ol', 'ul'].includes(node.nodeName);
export const isInlineTextElement = (node: Node): node is HTMLParagraphElement =>
  ['span', 'p', 'paragraph'].includes(node.nodeName) ||
  (['div'].includes(node.nodeName) && !Array.from(node.childNodes).some(isHtmlElement));

import type { CdaSection } from '@ctw/shared/content/document-viewer/_internal/parsers/types';

const xpathForNode = (node: Node): string => {
  if (!node?.parentNode) {
    return '';
  }

  // Handle document node (root)
  if (node.nodeType === Node.DOCUMENT_NODE) {
    return '/';
  }

  // Get the parent's xpath
  const parentXPath: string = xpathForNode(node.parentNode);

  // Get index of the current node among siblings with the same name
  let index = 1;
  let sibling = node.previousSibling;

  while (sibling) {
    if (sibling.nodeName === node.nodeName) {
      index++;
    }
    sibling = sibling.previousSibling;
  }

  // Build the XPath for the current node
  const nodePath = isTextNode(node) ? 'text()' : node.nodeName.toLowerCase();

  return `${parentXPath}/${nodePath}[${index}]`;
};

interface UniqueKeyForNodeOptions {
  section: CdaSection;
  context: string;
  node: Node;
  index?: number;
}

export const uniqueIdentifierForNode = ({
  section,
  context,
  node,
  index,
}: UniqueKeyForNodeOptions) => {
  return `${section.id}-${context}-${node.nodeName}-xpath|${xpathForNode(node)}${index ? `-${index}` : ''}`;
};
